"use client";

// ЕДИНЫЕ состояния экрана: «грузится», «пусто», «не удалось загрузить».
//
// ЗАЧЕМ. Аудит 08.08 нашёл 76 файлов со своим `animate-spin`, 77 мест со словом
// «Загрузка» и 166 мест с «Нет данных» / «Ничего не найдено» — каждое своей
// вёрсткой, кеглем и формулировкой. Пользователь видит один и тот же по смыслу
// экран, оформленный десятком разных способов.
//
// Формулировки тоже стандартизуем: без техножаргона, человеческим языком —
// не «Failed to fetch» и не «Ошибка 500», а что случилось и что делать.

import { useEffect, useState } from "react";
import type { ReactNode } from "react";

/** Крутилка. Отдельно — её вставляют внутрь кнопок и строк таблицы. */
export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <span
      role="status"
      aria-label="Загрузка"
      className={`inline-block shrink-0 animate-spin rounded-full ${className ?? ""}`}
      style={{
        width: size,
        height: size,
        border: `2px solid color-mix(in srgb, var(--primary) 25%, transparent)`,
        borderTopColor: "var(--primary)",
      }}
    />
  );
}

// ── Скелет (№16) ─────────────────────────────────────────────────────────────
// Прямоугольник по форме будущего содержимого. Высоту держит вызывающий: смысл
// скелета в том, чтобы блок НЕ менял размер, когда данные приедут.

export function Skeleton({
  width,
  height = 12,
  radius,
  className,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={`skeleton block ${className ?? ""}`}
      style={{ width: width ?? "100%", height, borderRadius: radius }}
    />
  );
}

/** Несколько строк подряд — список, таблица, абзац. */
export function SkeletonRows({
  rows = 6,
  height = 28,
  gap = 8,
  /** Последние строки короче: список выглядит как текст, а не как забор. */
  ragged = true,
  className,
}: {
  rows?: number;
  height?: number;
  gap?: number;
  ragged?: boolean;
  className?: string;
}) {
  return (
    <div className={`flex flex-col ${className ?? ""}`} style={{ gap }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={height} width={ragged && i === rows - 1 ? "62%" : "100%"} />
      ))}
    </div>
  );
}

// ── Лестница ожидания (№47) ──────────────────────────────────────────────────
// Единое правило портала на любое ожидание:
//   до 300 мс  — НИЧЕГО. Мигание спиннера на быстром ответе раздражает сильнее,
//                чем сама задержка, и создаёт ощущение дёрганого интерфейса;
//   до 1 с     — маленький спиннер: «принято, работаю»;
//   1–10 с     — скелет по форме содержимого. При той же реальной скорости
//                воспринимается примерно на треть быстрее спиннера;
//   дольше 10 с— полоса и текст, ЧТО именно идёт: на этой дистанции человек
//                уже решает, ждать ему или уйти, и ему нужен ответ на «сколько».
//
// Отсчёт идёт от монтирования: компоненты ожидания в портале и так живут ровно
// столько, сколько длится загрузка (`if (loading) return <Loading/>`).
export type WaitStage = "hidden" | "spinner" | "skeleton" | "bar";

export const WAIT_STEPS = { spinner: 300, skeleton: 1000, bar: 10000 } as const;

export function useWaitStage(): WaitStage {
  const [stage, setStage] = useState<WaitStage>("hidden");
  useEffect(() => {
    const timers = [
      window.setTimeout(() => setStage("spinner"), WAIT_STEPS.spinner),
      window.setTimeout(() => setStage("skeleton"), WAIT_STEPS.skeleton),
      window.setTimeout(() => setStage("bar"), WAIT_STEPS.bar),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, []);
  return stage;
}

/** Полоса ожидания. Без `value` — «дышит», с `value` (0…1) показывает процент. */
export function WaitBar({ value, className }: { value?: number; className?: string }) {
  const pct = value == null ? null : Math.max(0, Math.min(1, value));
  return (
    <div
      className={`wait-bar ${pct == null ? "is-indeterminate" : ""} ${className ?? ""}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct == null ? undefined : Math.round(pct * 100)}
    >
      <i style={pct == null ? undefined : { width: `${Math.round(pct * 100)}%` }} />
    </div>
  );
}

/** Экран/блок в состоянии загрузки — по лестнице ожидания.
 *
 *  Менять вызовы НЕ нужно: все существующие `<Loading />` получают лестницу
 *  просто потому, что она внутри. Раньше компонент сразу рисовал спиннер с
 *  подписью — на быстром ответе это давало вспышку, на медленном оставлял
 *  человека наедине с крутилкой на десять секунд.
 *
 *  `skeleton` — форма конкретного экрана (таблица, карточки, форма). Без неё
 *  берутся строки: лучше, чем схлопнутый блок, но хуже, чем свой силуэт, —
 *  так что на тяжёлых экранах силуэт стоит передать.
 */
export function Loading({
  text = "Загружаем данные…",
  compact = false,
  className,
  skeleton,
  progress,
}: {
  text?: string;
  /** Для небольших блоков (виджет, карточка) — меньше отступы. */
  compact?: boolean;
  className?: string;
  /** Свой силуэт вместо строк по умолчанию. */
  skeleton?: ReactNode;
  /** Доля выполненного (0…1), если она известна: полоса покажет процент. */
  progress?: number;
}) {
  const stage = useWaitStage();

  // До 300 мс не показываем ничего — но место держим, иначе блок дёрнется.
  if (stage === "hidden") return <div className={className} aria-hidden />;

  if (stage === "spinner") {
    return (
      <div
        className={`flex items-center justify-center ${compact ? "py-6" : "py-14"} ${className ?? ""}`}
        role="status"
        aria-label={text}
      >
        <Spinner size={compact ? 16 : 22} />
      </div>
    );
  }

  if (stage === "skeleton") {
    return (
      <div className={className} role="status" aria-label={text}>
        {skeleton ?? <SkeletonRows rows={compact ? 3 : 6} height={compact ? 20 : 28} />}
      </div>
    );
  }

  // Дольше десяти секунд: к силуэту добавляем полосу и говорим, что идёт.
  return (
    <div className={className} role="status" aria-label={text}>
      {skeleton ?? <SkeletonRows rows={compact ? 3 : 6} height={compact ? 20 : 28} />}
      <div className={compact ? "mt-3" : "mt-4"}>
        <WaitBar value={progress} />
        <div className="mt-1.5 text-[11px] text-[var(--textMuted)]">{text}</div>
      </div>
    </div>
  );
}

/** Пусто: данных нет — и это нормально (ещё не завели, фильтр ничего не нашёл). */
export function Empty({
  title = "Пока пусто",
  text,
  icon,
  action,
  compact = false,
  bordered = false,
  className,
}: {
  title?: ReactNode;
  /** Подсказка, что сделать, чтобы здесь что-то появилось. */
  text?: ReactNode;
  icon?: ReactNode;
  /** Кнопка действия — «Добавить», «Сбросить фильтры». */
  action?: ReactNode;
  compact?: boolean;
  /** Пунктирная рамка — когда пустое место само по себе блок (карточка,
   *  колонка), а не просто «в таблице ничего не нашлось». */
  bordered?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 text-center ${
        compact ? "py-6" : "py-10"
      } ${
        bordered ? "rounded-[var(--radius-lg)] border border-dashed border-[var(--border)] px-4" : ""
      } ${className ?? ""}`}
    >
      {icon && <div className="mb-1">{icon}</div>}
      <span className="widget-title text-[12px]">{title}</span>
      {text && <div className="max-w-[360px] text-[11px] leading-relaxed text-[var(--textMuted)]">{text}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Не удалось загрузить — с возможностью повторить. */
export function ErrorState({
  title = "Не удалось загрузить",
  text,
  onRetry,
  compact = false,
  className,
}: {
  title?: string;
  text?: ReactNode;
  onRetry?: () => void;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 text-center ${
        compact ? "py-6" : "py-14"
      } ${className ?? ""}`}
    >
      <span
        aria-hidden
        className="mb-1 flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold"
        style={{
          color: "var(--danger)",
          background: "color-mix(in srgb, var(--danger) 12%, transparent)",
        }}
      >
        !
      </span>
      <div className="text-[12px] font-semibold text-[var(--text)]">{title}</div>
      {text && <div className="max-w-[360px] text-[11px] leading-relaxed text-[var(--textMuted)]">{text}</div>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="glass-hover mt-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[12px] font-semibold"
          style={{ boxShadow: "none" }}
        >
          Повторить
        </button>
      )}
    </div>
  );
}
